/** @format */

import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Box, Typography } from "@mui/material";

import {
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
} from "../styles";
import { colors } from "../../../utility/types/constant";
import {
    IPredictionChart,
    PredictionChartType,
} from "../../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// PredictionCharts (Stage 2J) — renders the five prediction chart payloads
// produced by the server's predictionChartService. Each chart is a pure
// presentation of pre-computed, apexcharts-friendly data. No business logic.
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    charts: IPredictionChart[];
    currency?: string;
}

const FONT = "'Montserrat', sans-serif";

function fmtCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `$${Math.round(amount)}`;
    }
}

function fmtDate(value?: string | null): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}

function ChartCard({
    title,
    subtitle,
    children,
}: {
    title: string;
    subtitle: string;
    children: React.ReactNode;
}) {
    return (
        <CollectSectionCard>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>{title}</CollectSectionTitle>
                    <CollectSectionSubtitle>{subtitle}</CollectSectionSubtitle>
                </Box>
            </CollectSectionHeader>
            {children}
        </CollectSectionCard>
    );
}

// ── 1. Predicted probability (donut) ─────────────────────────────────────
const PredictedProbabilityChart: React.FC<{ data: Record<string, any> }> = ({
    data,
}) => {
    const series: number[] = Array.isArray(data?.series) ? data.series : [];
    const labels: string[] = Array.isArray(data?.labels)
        ? data.labels
        : ["On-time", "Late", "Non-payment risk"];

    const options: ApexOptions = {
        chart: { type: "donut", fontFamily: FONT },
        labels,
        colors: ["#16a34a", "#f59e0b", "#dc2626"],
        stroke: { width: 2, colors: ["#fff"] },
        legend: {
            position: "bottom",
            fontSize: "12px",
            markers: { width: 10, height: 10, radius: 5 },
        },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => `${Math.round(val)}%`,
            style: { fontSize: "11px", fontWeight: 600 },
        },
        tooltip: { y: { formatter: (val: number) => `${val}%` } },
        plotOptions: {
            pie: {
                donut: {
                    size: "65%",
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: "On-time",
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "#6b7280",
                            formatter: () => `${series[0] ?? 0}%`,
                        },
                    },
                },
            },
        },
    };

    return (
        <ChartCard
            title="Predicted outcome"
            subtitle="Estimated split between paying on time, paying late, and non-payment risk."
        >
            <Chart options={options} series={series} type="donut" height={260} />
        </ChartCard>
    );
};

// ── 2. Payment reliability trend (bar) ───────────────────────────────────
const ReliabilityTrendChart: React.FC<{ data: Record<string, any> }> = ({
    data,
}) => {
    const categories: string[] = Array.isArray(data?.categories)
        ? data.categories
        : ["On-time", "Late"];
    const series = Array.isArray(data?.series) ? data.series : [];
    const onTimeRate: number | null =
        typeof data?.onTimeRate === "number" ? data.onTimeRate : null;
    const recentOnTimeRate: number | null =
        typeof data?.recentOnTimeRate === "number" ? data.recentOnTimeRate : null;

    const options: ApexOptions = {
        chart: { type: "bar", fontFamily: FONT, toolbar: { show: false } },
        colors: ["#16a34a", "#dc2626"],
        plotOptions: {
            bar: {
                distributed: true,
                borderRadius: 6,
                columnWidth: "45%",
            },
        },
        dataLabels: { enabled: true, style: { fontSize: "12px", fontWeight: 700 } },
        legend: { show: false },
        xaxis: {
            categories,
            labels: { style: { fontFamily: FONT, fontSize: "12px" } },
        },
        yaxis: {
            labels: { style: { fontFamily: FONT, fontSize: "12px" } },
        },
        tooltip: {
            y: {
                formatter: (val: number) =>
                    `${val} settled invoice${val === 1 ? "" : "s"}`,
            },
        },
    };

    return (
        <ChartCard
            title="Past payment reliability"
            subtitle="How this customer has settled invoices historically."
        >
            <Chart options={options} series={series} type="bar" height={260} />
            {(onTimeRate !== null || recentOnTimeRate !== null) && (
                <Box sx={{ display: "flex", gap: 3, mt: 1, flexWrap: "wrap" }}>
                    {onTimeRate !== null && (
                        <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#475569" }}>
                            All-time on-time rate:{" "}
                            <strong style={{ color: colors.primary }}>{onTimeRate}%</strong>
                        </Typography>
                    )}
                    {recentOnTimeRate !== null && (
                        <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#475569" }}>
                            Recent on-time rate:{" "}
                            <strong style={{ color: colors.primary }}>{recentOnTimeRate}%</strong>
                        </Typography>
                    )}
                </Box>
            )}
        </ChartCard>
    );
};

// ── 3. Accountability breakdown (horizontal bar) ─────────────────────────
const AccountabilityChart: React.FC<{ data: Record<string, any> }> = ({
    data,
}) => {
    const categories: string[] = Array.isArray(data?.categories)
        ? data.categories
        : [];
    const series = Array.isArray(data?.series) ? data.series : [];
    const recommendedActionLabel: string | undefined =
        typeof data?.recommendedActionLabel === "string"
            ? data.recommendedActionLabel
            : undefined;

    const values: number[] = series[0]?.data ?? [];
    const palette = values.map((v) => (v >= 0 ? "#16a34a" : "#dc2626"));

    const options: ApexOptions = {
        chart: { type: "bar", fontFamily: FONT, toolbar: { show: false } },
        colors: palette,
        plotOptions: {
            bar: {
                horizontal: true,
                distributed: true,
                borderRadius: 5,
                barHeight: "60%",
            },
        },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => `${val > 0 ? "+" : ""}${val}`,
            style: { fontSize: "11px", fontWeight: 600 },
        },
        legend: { show: false },
        xaxis: {
            categories,
            labels: { style: { fontFamily: FONT, fontSize: "11px" } },
        },
        yaxis: { labels: { style: { fontFamily: FONT, fontSize: "12px" } } },
        tooltip: {
            y: {
                formatter: (val: number) =>
                    `${val > 0 ? "+" : ""}${val} pts toward on-time score`,
            },
        },
    };

    return (
        <ChartCard
            title="What's driving this prediction"
            subtitle="Each factor's weighted contribution to the on-time score (green helps, red hurts)."
        >
            <Chart options={options} series={series} type="bar" height={300} />
            {recommendedActionLabel && (
                <Box
                    sx={{
                        mt: 1.5,
                        p: "10px 14px",
                        borderRadius: "8px",
                        backgroundColor: "rgba(30, 193, 149, 0.1)",
                        border: "1px solid rgba(30, 193, 149, 0.3)",
                    }}
                >
                    <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#0f7a5b", fontWeight: 600 }}>
                        Suggested next step: {recommendedActionLabel}
                    </Typography>
                </Box>
            )}
        </ChartCard>
    );
};

// ── 4. What-if cash impact (stacked bar) ─────────────────────────────────
const CashImpactChart: React.FC<{ data: Record<string, any> }> = ({ data }) => {
    const currency: string = data?.currency || "USD";
    const categories: string[] = Array.isArray(data?.categories)
        ? data.categories
        : ["Baseline"];
    const series = Array.isArray(data?.series) ? data.series : [];

    const options: ApexOptions = {
        chart: {
            type: "bar",
            stacked: true,
            fontFamily: FONT,
            toolbar: { show: false },
        },
        colors: ["#16a34a", "#dc2626"],
        plotOptions: { bar: { borderRadius: 6, columnWidth: "40%" } },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => fmtCurrency(val, currency),
            style: { fontSize: "11px", fontWeight: 600 },
        },
        legend: { position: "bottom", fontSize: "12px" },
        xaxis: {
            categories,
            labels: { style: { fontFamily: FONT, fontSize: "12px" } },
        },
        yaxis: {
            labels: {
                formatter: (val: number) => fmtCurrency(val, currency),
                style: { fontFamily: FONT, fontSize: "11px" },
            },
        },
        tooltip: { y: { formatter: (val: number) => fmtCurrency(val, currency) } },
    };

    return (
        <ChartCard
            title="Projected receivables impact"
            subtitle="Expected recovered amount versus the amount still at risk."
        >
            <Chart options={options} series={series} type="bar" height={260} />
        </ChartCard>
    );
};

// ── 5. Receivables timeline (visual milestones) ──────────────────────────
const ReceivablesTimeline: React.FC<{ data: Record<string, any> }> = ({
    data,
}) => {
    const currency: string = data?.currency || "USD";
    const amount: number =
        typeof data?.amount === "number" ? data.amount : 0;
    const points: Array<{ label: string; date: string }> = Array.isArray(
        data?.points,
    )
        ? data.points
        : [];
    const expectedDaysLate: number =
        typeof data?.expectedDaysLate === "number" ? data.expectedDaysLate : 0;

    return (
        <ChartCard
            title="Expected payment window"
            subtitle="When the outstanding amount is likely to be settled."
        >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.6rem", color: colors.primary }}>
                    {fmtCurrency(amount, currency)}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#6b7280" }}>
                    expected{" "}
                    {expectedDaysLate > 0
                        ? `~${expectedDaysLate} day${expectedDaysLate === 1 ? "" : "s"} after due`
                        : "on or before the due date"}
                </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {points.map((p, idx) => (
                    <Box
                        key={`${p.label}-${idx}`}
                        sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                alignSelf: "stretch",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    mt: "4px",
                                    backgroundColor:
                                        p.label === "Expected"
                                            ? colors.mainSecondary
                                            : "#cbd5e1",
                                    border: "2px solid #fff",
                                    boxShadow: "0 0 0 1px #cbd5e1",
                                }}
                            />
                            {idx < points.length - 1 && (
                                <Box sx={{ width: "2px", flex: 1, minHeight: 24, backgroundColor: "#e2e8f0" }} />
                            )}
                        </Box>
                        <Box sx={{ pb: 2 }}>
                            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "0.85rem", color: "#1f2937" }}>
                                {p.label}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT, fontSize: "0.8rem", color: "#6b7280" }}>
                                {fmtDate(p.date)}
                            </Typography>
                        </Box>
                    </Box>
                ))}
                {points.length === 0 && (
                    <Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "#6b7280" }}>
                        Not enough data to estimate a payment window.
                    </Typography>
                )}
            </Box>
        </ChartCard>
    );
};

const PredictionCharts: React.FC<Props> = ({ charts }) => {
    const byType = useMemo(() => {
        const map = new Map<PredictionChartType, IPredictionChart>();
        (charts || []).forEach((c) => map.set(c.type, c));
        return map;
    }, [charts]);

    const probability = byType.get("predicted_probability");
    const reliability = byType.get("payment_reliability_trend");
    const accountability = byType.get("accountability_breakdown");
    const cashImpact = byType.get("whatif_cash_impact");
    const timeline = byType.get("receivables_timeline");

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 0,
                columnGap: 3,
            }}
        >
            {probability && <PredictedProbabilityChart data={probability.data} />}
            {reliability && <ReliabilityTrendChart data={reliability.data} />}
            {timeline && <ReceivablesTimeline data={timeline.data} />}
            {cashImpact && <CashImpactChart data={cashImpact.data} />}
            {accountability && (
                <Box sx={{ gridColumn: { md: "1 / -1" } }}>
                    <AccountabilityChart data={accountability.data} />
                </Box>
            )}
        </Box>
    );
};

export default PredictionCharts;
